import moment, { Moment } from 'moment'
import { isNil, isEqual } from 'lodash'
import { CalendarType, LocationType } from 'pages/my-calendars/enums'
import { OrderDay, PropertyAdditional } from 'pages/my-calendars/add-calendar'
import {
    ICalendar,
    IDate,
    LocationCalendars,
    LocationDates,
    AdditionalFields,
    UnavailableDay,
    WeekDay
} from 'pages/my-calendars/add-calendar/interfaces'
import {
    DAY_STRING_FORMAT,
    AMERICAN_DATE_FORMAT,
    MAY_HOLIDAYS_KEY,
    NEW_YEAR_KEY,
    DELAY_BEFORE_AVAILABLE_DAYS
} from 'pages/my-calendars/constants'
import { prevDaysMap } from 'shared/consts'
import {
    getDisableFilteredDates,
    getFilteredApprovedDates,
    isApprovedFilteredDates
} from './approvedCalendars'

export const isInfoCalendar = (type: CalendarType) => type === CalendarType.INFO

export const isCurrentDateUnavailable = (
    currentDate: Moment,
    disableDatesProperty: 'unavailableDays' | 'orderDays',
    disableDates?: {
        orderDays: OrderDay[]
        unavailableDays: UnavailableDay[]
    }
) => {
    if (!disableDates || disableDatesProperty !== 'unavailableDays') {
        return false
    }

    return disableDates[disableDatesProperty].some(
        //eslint-disable-next-line @typescript-eslint/no-explicit-any
        (disableDate: any) =>
            disableDate[
                disableDatesProperty === 'unavailableDays' ? 'weekDay' : 'orderDay'
            ].toLowerCase() === currentDate.locale('en').format(DAY_STRING_FORMAT).toLowerCase()
    )
}

export const filterItemsWithDates = (calendars: LocationCalendars[]) => {
    const result = calendars
        .map(({ additionalFields, ...rest }) => {
            // Создаем копию массива additionalFields с фильтрацией
            const filteredAdditionalFields = additionalFields.filter((field) => {
                // Фильтруем элементы, у которых есть orderDate или deliveryDate
                return field.orderDate || field.deliveryDate
            })

            // Сортировка по orderDate или deliveryDate с проверкой на null
            const sortedAdditionalFields = filteredAdditionalFields.sort((a, b) => {
                const dateA = a.orderDate || a.deliveryDate
                const dateB = b.orderDate || b.deliveryDate
                // Сравниваем даты с учётом возможных null значений
                return dateA && dateB ? new Date(dateA).getTime() - new Date(dateB).getTime() : 0
            })

            // Ограничиваем длину массива до 2 элементов
            const finalAdditionalFields = sortedAdditionalFields.slice(0, 2)

            return {
                ...rest, // Копируем остальные свойства
                additionalFields: finalAdditionalFields // Обновляем additionalFields
            }
        })
        // Отфильтровываем те элементы, у которых нет данных в основных полях, и дополнительного контента
        .filter(
            (item) =>
                (item.lastOrderDate !== null && item.lastOrderDate !== undefined) ||
                (item.firstOrderDate !== null && item.firstOrderDate !== undefined) ||
                (item.orderDate !== null && item.orderDate !== undefined) ||
                (item.deliveryDate !== null && item.deliveryDate !== undefined)
        )
    return result
}

export const isMayOrNewYear = (
    currentDate: Moment,
    isMayOrNewYearHoliday?: {
        type: string
        status: boolean
    }
) => {
    let isUnavailablePeriod = false

    if (isMayOrNewYearHoliday && isMayOrNewYearHoliday.status) {
        if (isMayOrNewYearHoliday.type.includes(NEW_YEAR_KEY)) {
            isUnavailablePeriod =
                currentDate.month() !== 10 &&
                currentDate.month() !== 11 &&
                currentDate.month() !== 0
        } else if (isMayOrNewYearHoliday.type.includes(MAY_HOLIDAYS_KEY)) {
            isUnavailablePeriod = currentDate.month() !== 3 && currentDate.month() !== 4
        }
    }

    return isUnavailablePeriod
}

export const isLastOrderDateDisabled = (
    currentDate: Moment,
    prevDays: WeekDay[],
    copyArrayCalendar: LocationCalendars,
    disableFilteredDates: Date[][],
    disableDates?: {
        orderDays: OrderDay[]
        unavailableDays: UnavailableDay[]
        lastOrderDate: string
        firstOrderDate: string
    }
) => {
    const firstOrderValue: Moment | null = copyArrayCalendar?.firstOrderDate
        ? moment(copyArrayCalendar?.firstOrderDate)
        : null

    const isMay = isMayOrNewYear(currentDate)
    const isCDU = isCurrentDateUnavailable(currentDate, 'orderDays', disableDates)
    const isRMS = onlyRmsOrderDate(currentDate, prevDays, disableDates)
    const isSameDates =
        currentDate &&
        !!firstOrderValue &&
        moment(currentDate).isSameOrAfter(firstOrderValue, 'day')
    const isTooEarlyToOrder =
        currentDate && currentDate < moment().add(DELAY_BEFORE_AVAILABLE_DAYS, 'days')

    // RFS251202_4815: отключить функционал проверки наличия дней недоступности магазинов
    /*const isBetweenLastAndFirstOrderDates =
        currentDate &&
        moment(disableDates?.lastOrderDate) < currentDate &&
        currentDate < moment(disableDates?.firstOrderDate)*/

    const { isApprovedDates } = isApprovedFilteredDates(disableFilteredDates, currentDate)

    return isMay || isCDU || isRMS || isSameDates || isTooEarlyToOrder || isApprovedDates
}

export const isFirstOrderDateDisabled = (
    currentDate: Moment,
    prevDays: WeekDay[],
    copyArrayCalendar: LocationCalendars,
    disableFilteredDates: Date[][],
    disableDates?: {
        orderDays: OrderDay[]
        unavailableDays: UnavailableDay[]
        lastOrderDate: string
        firstOrderDate: string
    },
    lastOrderDate?: Moment
) => {
    const lastOrderValue: Moment | null = copyArrayCalendar?.lastOrderDate
        ? moment(copyArrayCalendar?.lastOrderDate)
        : null

    const isMay = isMayOrNewYear(currentDate)
    const isCDU = isCurrentDateUnavailable(currentDate, 'orderDays', disableDates)
    const isRMS = onlyRmsOrderDate(currentDate, prevDays, disableDates)
    const isSameDates =
        currentDate && !!lastOrderValue && moment(currentDate).isSameOrBefore(lastOrderValue, 'day')
    const isMoreThan36DaysAfterLastOrder =
        currentDate &&
        !!lastOrderValue &&
        currentDate >= moment(lastOrderValue.format(AMERICAN_DATE_FORMAT)).add(36, 'days')

    // RFS251202_4815: отключить функционал проверки наличия дней недоступности магазинов
    /*const isBetweenLastAndFirstOrderDates =
        currentDate &&
        moment(disableDates?.lastOrderDate) < currentDate &&
        currentDate < moment(disableDates?.firstOrderDate)*/

    const { isApprovedDates } = isApprovedFilteredDates(
        disableFilteredDates,
        currentDate,
        lastOrderDate
    )

    return (
        !lastOrderValue ||
        isMay ||
        isCDU ||
        isRMS ||
        isSameDates ||
        isMoreThan36DaysAfterLastOrder ||
        isApprovedDates
    )
}

export const isDeliveryDateDisabled = (
    currentDate: Moment,
    prevDays: WeekDay[],
    copyArrayCalendar: LocationCalendars,
    disableFilteredDates: Date[][],
    disableDates?: {
        orderDays: OrderDay[]
        unavailableDays: UnavailableDay[]
        lastOrderDate: string
        firstOrderDate: string
    },
    deliveryType?: string,
    additionalId?: string
) => {
    const lastOrderValue: Moment | null = copyArrayCalendar?.lastOrderDate
        ? moment(copyArrayCalendar?.lastOrderDate)
        : null
    const firstOrderValue: Moment | null = copyArrayCalendar?.firstOrderDate
        ? moment(copyArrayCalendar?.firstOrderDate)
        : null
    const deliveryValue: Moment | null = copyArrayCalendar?.deliveryDate
        ? moment(copyArrayCalendar?.deliveryDate)
        : null

    const isMay = isMayOrNewYear(currentDate)
    const isCDU = isCurrentDateUnavailable(currentDate, 'orderDays', disableDates)
    const isBBXD =
        deliveryType === 'BBXD' ? onlyRmsOrderDate(currentDate, prevDays, disableDates) : ''
    const isSameAsLastOrderDate = lastOrderValue && currentDate.diff(lastOrderValue, 'day') === 0 // deliveryDate cant be lastOrderDate CDCALENDAR-51
    const isOutsideValidOrderInterval =
        currentDate &&
        !!firstOrderValue &&
        !!lastOrderValue &&
        (currentDate >= firstOrderValue || currentDate <= lastOrderValue)
    const { isApprovedDates } = isApprovedFilteredDates(disableFilteredDates, currentDate)

    return (
        !lastOrderValue ||
        !firstOrderValue ||
        (additionalId && !deliveryValue) ||
        isMay ||
        isCDU ||
        isBBXD ||
        isSameAsLastOrderDate ||
        isOutsideValidOrderInterval ||
        isApprovedDates
    )
}

export const isUnavailableDateDisabled = (
    currentDate: Moment,
    copyArrayCalendar: LocationCalendars,
    disableFilteredDates: Date[][],
    disableDates?: {
        orderDays: OrderDay[]
        unavailableDays: UnavailableDay[]
        lastOrderDate: string
        firstOrderDate: string
    },
    additionalId?: string
) => {
    const lastOrderValue: Moment | null = copyArrayCalendar?.lastOrderDate
        ? moment(copyArrayCalendar?.lastOrderDate)
        : null
    const firstOrderValue: Moment | null = copyArrayCalendar?.firstOrderDate
        ? moment(copyArrayCalendar?.firstOrderDate)
        : null
    const deliveryValue: Moment | null = copyArrayCalendar?.deliveryDate
        ? moment(copyArrayCalendar?.deliveryDate)
        : null
    const orderValue: Moment | null = copyArrayCalendar?.orderDate
        ? moment(copyArrayCalendar?.orderDate)
        : null

    const additionalCalendars = copyArrayCalendar?.additionalFields
    const additionalDeliveryValue = additionalCalendars.filter((el) => el.index === additionalId)[0]
        ?.deliveryDate
    const deliveryDateValue = additionalId ? moment(additionalDeliveryValue) : deliveryValue

    const isMay = isMayOrNewYear(currentDate)
    const isCDU = isCurrentDateUnavailable(currentDate, 'unavailableDays', disableDates)
    const isBeforeDeliveryDate =
        currentDate && deliveryDateValue !== null && currentDate <= deliveryDateValue
    const isMoreThanOneMonthAfterDeliveryDate =
        currentDate &&
        !!deliveryDateValue &&
        currentDate >= moment(deliveryDateValue.format(AMERICAN_DATE_FORMAT)).add(1, 'month')
    const { isApprovedDates } = isApprovedFilteredDates(disableFilteredDates, currentDate)

    return (
        !lastOrderValue ||
        !firstOrderValue ||
        !deliveryValue ||
        !deliveryDateValue ||
        (additionalId && !orderValue) ||
        isMay ||
        isCDU ||
        isBeforeDeliveryDate ||
        isMoreThanOneMonthAfterDeliveryDate ||
        isApprovedDates
    )
}

export const getDateWithOffset = (date: Date) => {
    const momentDate = moment(date)
    const offset = momentDate.utcOffset()
    return momentDate.add(Math.abs(offset), 'minutes')
}

export interface IGetDateValue {
    date: Date
    disableDates: {
        orderDays: OrderDay[]
        unavailableDays: UnavailableDay[]
    }
    copyArrayCalendar: LocationCalendars
    approvedData: ICalendar[]
    worksheetData: ICalendar[]
    locationId?: number
    property?: keyof LocationDates
    unavailableDays?: {
        orderDays: OrderDay[]
        unavailableDays: UnavailableDay[]
        lastOrderDate: string
        firstOrderDate: string
    }
    deliveryType?: string
    calendarId?: string
    lastOrderDate?: Moment
    additionalId?: string
}

export const getDateValue = (props: IGetDateValue) => {
    const {
        date,
        disableDates,
        copyArrayCalendar,
        approvedData,
        worksheetData,
        locationId,
        property,
        unavailableDays,
        deliveryType,
        calendarId,
        lastOrderDate,
        additionalId
    } = props
    const currDate = getDateWithOffset(date)
    const dateObj = currDate.toDate()
    const prevDays: Array<WeekDay> = (disableDates?.orderDays || []).map(
        (i) => prevDaysMap[i.orderDay]
    )

    const { filteredApprovedDates } = getFilteredApprovedDates(
        approvedData,
        worksheetData,
        locationId,
        calendarId
    )
    const { disableFilteredDates } = getDisableFilteredDates(filteredApprovedDates)

    if (property === 'lastOrderDate') {
        if (
            isLastOrderDateDisabled(
                currDate,
                prevDays,
                copyArrayCalendar,
                disableFilteredDates,
                unavailableDays
            )
        ) {
            return null
        } else {
            return dateObj
        }
    } else if (property === 'firstOrderDate') {
        if (
            isFirstOrderDateDisabled(
                currDate,
                prevDays,
                copyArrayCalendar,
                disableFilteredDates,
                unavailableDays,
                lastOrderDate
            )
        ) {
            return null
        } else {
            return dateObj
        }
    } else if (property === 'deliveryDate') {
        if (additionalId) {
            if (
                isDeliveryDateDisabled(
                    currDate,
                    prevDays,
                    copyArrayCalendar,
                    disableFilteredDates,
                    unavailableDays,
                    deliveryType,
                    additionalId
                )
            ) {
                return null
            } else {
                return dateObj
            }
        } else {
            if (
                isDeliveryDateDisabled(
                    currDate,
                    prevDays,
                    copyArrayCalendar,
                    disableFilteredDates,
                    unavailableDays,
                    deliveryType
                )
            ) {
                return null
            } else {
                return dateObj
            }
        }
    } else if (property === 'orderDate') {
        if (additionalId) {
            if (
                isUnavailableDateDisabled(
                    currDate,
                    copyArrayCalendar,
                    disableFilteredDates,
                    unavailableDays,
                    additionalId
                )
            ) {
                return null
            } else {
                return dateObj
            }
        } else {
            if (
                isUnavailableDateDisabled(
                    currDate,
                    copyArrayCalendar,
                    disableFilteredDates,
                    unavailableDays
                )
            ) {
                return null
            } else {
                return dateObj
            }
        }
    }

    return null
}

export interface DatesObject {
    firstOrderDate: Date | null
    lastOrderDate: Date | null
    orderDate: Date | null
    deliveryDate: Date | null
    additionalFields: PropertyAdditional[]
}

export const getFilledAdditionalFields = (datesObject: DatesObject) => {
    return datesObject.additionalFields.map((field) => {
        if (
            !datesObject.lastOrderDate ||
            !datesObject.firstOrderDate ||
            !datesObject.deliveryDate
        ) {
            return {
                ...field,
                deliveryDate: null,
                orderDate: null
            }
        } else {
            return field
        }
    })
}

export const getDefaultLocationCalendar = () => ({
    id: '',
    locationId: -1,
    locationType: LocationType.All,
    isNeedToErase: false,
    lastOrderDate: null,
    firstOrderDate: null,
    orderDate: null,
    deliveryDate: null,
    additionalFields: []
})

export const onlyRmsOrderDate = (
    currentDate: Moment,
    prevDays: WeekDay[],
    disableDates?: {
        orderDays: OrderDay[]
        unavailableDays: UnavailableDay[]
    }
) => {
    return disableDates
        ? prevDays.includes(currentDate.locale('en').format(DAY_STRING_FORMAT) as WeekDay)
        : false
}

/**
 * Функция возвращает массив изменённых пользователем полей календаря.
 *
 * @param {IDate[] | undefined} prevDates - предыдущее состояние календаря из Redux.
 * @param {LocationDates[]} newDates - обновленное локальное состояние календаря.
 *
 * @returns {LocationDates[]} updatedDates
 */
export const getUpdatedDates = (
    prevDates: IDate[] | undefined,
    newDates: LocationDates[]
): LocationDates[] => {
    const formattedPrevDates = prevDates?.map((item) => ({
        deliveryDate: item.deliveryDate ? new Date(item.deliveryDate) : null,
        firstOrderDate: item.firstOrderDate ? new Date(item.firstOrderDate) : null,
        id: item.id,
        lastOrderDate: item.lastOrderDate ? new Date(item.lastOrderDate) : null,
        orderDate: item.orderDate ? new Date(item.orderDate) : null
    }))

    const dateset = newDates.map((item) => ({
        id: item.id,
        deliveryDate: item.deliveryDate,
        orderDate: item.orderDate,
        firstOrderDate: item.firstOrderDate,
        lastOrderDate: item.lastOrderDate
    }))

    const updatedDates: LocationDates[] = dateset.reduce((acc: LocationDates[], curr) => {
        const prevDate = formattedPrevDates?.find((item) => item.id === curr.id)

        return isEqual(prevDate, curr) ? acc : [...acc, curr]
    }, [])

    return updatedDates
}

/**
 * Функция возвращает массив уникальных дополнительных полей в каледаре
 *
 * @param {keyof LocationDates} property - заполняемое поле в календаре,
 * @param {PropertyAdditional[]} additionalFields - массив с повторяющимися дополнительными полями.
 */
export const getUniqAdditionalFields = (
    property: keyof LocationDates,
    additionalFields: PropertyAdditional[]
) => {
    return additionalFields.reduce(
        (
            acc: {
                tempObj: Record<string, boolean>
                fields: PropertyAdditional[]
            },
            field: PropertyAdditional
        ) => {
            const currentDate = property === 'deliveryDate' ? field.deliveryDate : field.orderDate
            const str = currentDate ? currentDate.toLocaleDateString('ru-RU') : ''

            if (str && acc.tempObj[str]) {
                return acc
            }

            if (str) {
                acc.tempObj[str] = true
            }
            acc.fields.push(field)
            return acc
        },
        {
            tempObj: {},
            fields: []
        }
    ).fields
}

/**
 * Удаляет дополнительные поля, у которых не заполнены даты доставки и заказа.
 * Возвращает новый массив только с заполненными полями.
 *
 * @param {AdditionalFields[]} additionalFields - Массив дополнительных полей
 * @returns {AdditionalFields[]} - Отфильтрованный массив без пустых полей
 */
export const deleteEmptyAdditionalFields = (
    additionalFields: AdditionalFields[]
): AdditionalFields[] => {
    return additionalFields.filter((item) => !(isNil(item.deliveryDate) && isNil(item.orderDate)))
}

export const createCalendarMapItem = (
    dates: IDate[],
    prev: { [key: string]: LocationDates },
    curr: IDate
): { [key: string]: LocationDates } => {
    if (prev[curr.locationId]) {
        const additionalFields = dates
            .filter((el) => el.locationId === curr.locationId)
            .slice(1)
            .map((el, index) => ({
                id: el.id,
                index: index.toString(),
                orderDate: el.orderDate ? new Date(el.orderDate) : null,
                deliveryDate: el.deliveryDate ? new Date(el.deliveryDate) : null
            }))

        return {
            ...prev,
            [curr.locationId]: {
                ...prev[curr.locationId],
                additionalFields
            }
        }
    }
    return {
        ...prev,
        [curr.locationId]: {
            id: curr.id ? curr.id : '',
            lastOrderDate: curr.lastOrderDate ? new Date(curr.lastOrderDate) : null,
            firstOrderDate: curr.firstOrderDate ? new Date(curr.firstOrderDate) : null,
            orderDate: curr.orderDate ? new Date(curr.orderDate) : null,
            deliveryDate: curr.deliveryDate ? new Date(curr.deliveryDate) : null
        }
    }
}
